<template>
  <v-container fluid>
    <v-card>
      <v-system-bar color="device" />
      <v-toolbar flat>
        <v-icon left large color="device">mdi-camera-control</v-icon>
        <v-toolbar-title>
          デバイス
        </v-toolbar-title>
      </v-toolbar>
      <v-dialog
        v-model="editUnitDialog"
        max-width="600px"
        transition="slide-x-reverse-transition"
      >
        <device-unit-edit-dialog
          :selectedDevice="selectedDevice"
          :selectedUnit="selectedUnit"
          :devices="devices"
          @clickClear="closeAllDialogs"
          @updateDevice="saveDevice"
        />
      </v-dialog>
      <v-dialog
        v-model="editGroupDialog"
        max-width="600px"
        transition="slide-x-reverse-transition"
      >
        <device-group-edit-dialog
          :selectedDevice="selectedDevice"
          :selectedGroup="selectedGroup"
          :devices="devices"
          @clickClear="closeAllDialogs"
          @updateDevice="saveDevice"
        />
      </v-dialog>
      <v-dialog
        v-model="createUnitDialog"
        max-width="600px"
        transition="slide-x-reverse-transition"
      >
        <device-unit-create-dialog
          :units="units"
          :devices="devices"
          @clickClear="closeAllDialogs"
          @updateDevice="saveDevice"
        />
      </v-dialog>
      <v-dialog
        v-model="createGroupDialog"
        max-width="600px"
        transition="slide-x-reverse-transition"
      >
        <device-group-create-dialog
          :groups="groups"
          :units="units"
          :devices="devices"
          @clickClear="closeAllDialogs"
          @updateDevice="saveDevice"
        />
      </v-dialog>
      <v-dialog
        v-model="scheduleDialog"
        max-width="800px"
        transition="slide-x-reverse-transition"
      >
        <device-schedule-dialog
          :device="selectedDevice"
          :events="events"
          :devices="devices"
          @close="closeAllDialogs"
        />
      </v-dialog>

      <v-data-table
        :headers="headers"
        :items="devices"
        :loading="devicesLoading"
        loading-text="ロード中"
        no-data-text="データがありません"
        class="pa-4"
      >
        <!-- Table for managing units and groups -->
        <template v-slot:top>
          <v-container>
            <v-row dense no-gutters>
              <v-col
                cols="auto"
                xl="1"
                lg="1"
                md="12"
                sm="12"
                xs="12"
                align-self="center"
                ><v-icon left>mdi-orbit</v-icon><span>ユニット:</span></v-col
              ><v-col cols="auto" xl="11" lg="11" md="12" sm="12" xs="12"
                ><v-card class="d-flex flex-row elevation-0">
                  <v-chip
                    label
                    class="ma-2"
                    active-class="unit--text"
                    :input-value="isAllUnitFilter"
                  >
                    ALL
                  </v-chip>
                  <v-chip-group
                    multiple
                    active-class="unit--text"
                    v-model="unitFilters"
                  >
                    <v-chip label v-for="unit in units" :key="unit.id">
                      {{ unit.name }}
                    </v-chip>
                  </v-chip-group>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        @click="createUnitDialog = true"
                        fab
                        x-small
                        class="mt-2"
                        color="unit"
                      >
                        <v-icon> mdi-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>ユニット追加</span>
                  </v-tooltip>
                </v-card>
              </v-col>
            </v-row>

            <v-row dense no-gutters>
              <v-col
                cols="auto"
                xl="1"
                lg="1"
                md="12"
                sm="12"
                xs="12"
                align-self="center"
                ><v-icon left medium>mdi-folder</v-icon
                ><span>グループ:</span></v-col
              ><v-col cols="auto" xl="11" lg="11" md="12" sm="12" xs="12"
                ><v-card class="d-flex flex-row elevation-0">
                  <v-chip
                    label
                    class="ma-2"
                    active-class="group--text"
                    :input-value="isAllGroupFilter"
                  >
                    ALL
                  </v-chip>
                  <v-chip-group
                    multiple
                    active-class="group--text"
                    v-model="groupFilters"
                  >
                    <v-chip label v-for="group in groups" :key="group.id">
                      {{ group.name }}
                    </v-chip>
                  </v-chip-group>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        @click="createGroupDialog = true"
                        fab
                        x-small
                        class="mt-2"
                        color="group"
                      >
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>グループ追加</span>
                  </v-tooltip>
                </v-card>
              </v-col>
            </v-row>
            <v-row> </v-row>
          </v-container>
        </template>

        <!-- Table for device settings -->
        <template v-slot:[`item.name`]="{ item }">
          <v-edit-dialog
            large
            :return-value.sync="item.name"
            @save="saveDevice(item)"
          >
            <v-btn small icon left><v-icon>mdi-playlist-edit</v-icon></v-btn>
            <span>{{ item.name }}</span>
            <template v-slot:input>
              <v-text-field
                :rules="[v => v.length >= 3 || '3文字以上']"
                v-model="item.name"
                single-line
                autofocus
              />
            </template>
          </v-edit-dialog>
        </template>
        <template v-slot:[`item.id`]="{ item }">
          {{ item.id }}
        </template>

        <template v-slot:[`item.units`]="{ item }">
          <v-sheet max-width="240" color="transparent">
            <v-chip-group column>
              <v-chip
                v-for="unitId in item.units"
                :key="unitId"
                color="unit"
                close
                @click="editUnit(item, unitId)"
                @click:close="removeUnitFromDevice(item, unitId)"
                class="white--text ma-1 elevation-1"
                label
              >
                {{ getDeviceUnitName(unitId) }}
              </v-chip>
            </v-chip-group>
          </v-sheet>
          <v-menu
            bottom
            :ref="'unit-' + item.id"
            transition="slide-x-transition"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                color="unit"
                v-on="on"
                @click="$refs['unit-' + item.id].value = true"
                ><v-icon>mdi-plus</v-icon></v-btn
              >
            </template>
            <device-unit-create-dialog
              :units="units"
              :devices="devices"
              :isAllAddMode="false"
              :selectedDevice="item"
              :editDialog="createUnitDialog"
              @clickClear="clearMenu(item)"
              @updateDevice="saveDevice"
              class="ma-0"
            />
          </v-menu>
        </template>

        <template v-slot:[`item.groups`]="{ item }">
          <v-sheet max-width="240" color="transparent">
            <v-chip-group column>
              <v-chip
                v-for="groupId in item.groups"
                :key="groupId"
                color="group"
                close
                class="white--text ma-1 elevation-1"
                @click="editGroup(item, groupId)"
                @click:close="removeGroupFromDevice(item, groupId)"
                label
              >
                <span class="white--text">{{
                  getDeviceGroupName(groupId)
                }}</span>
              </v-chip>
            </v-chip-group>
          </v-sheet>
          <v-menu
            bottom
            :ref="'group-' + item.id"
            transition="slide-x-transition"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                color="group"
                v-on="on"
                @click="$refs['group-' + item.id].value = true"
                ><v-icon>mdi-plus</v-icon></v-btn
              >
            </template>
            <device-group-create-dialog
              :groups="groups"
              :units="units"
              :devices="devices"
              :selectedDevice="item"
              :isAllAddMode="false"
              @clickClear="clearMenu(item)"
              @updateDevice="saveDevice"
              class="ma-0"
            />
          </v-menu>
        </template>

        <template v-slot:[`item.mode`]="{ item }">
          <v-edit-dialog
            large
            :return-value.sync="item.mode"
            @save="updateMode(deviceComposition.single, item.id, item.mode)"
          >
            <v-btn small icon left><v-icon>mdi-playlist-edit</v-icon></v-btn>
            <span>{{ appModeName(item.mode) }}</span>
            <template v-slot:input>
              <v-select
                v-model="item.mode"
                :items="appModes"
                item-text="name"
                item-value="mode"
              />
            </template>
          </v-edit-dialog>
        </template>

        <template v-slot:[`item.online`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon large v-on="on" :color="item.online ? 'success' : 'error'"
                >mdi-wifi</v-icon
              >
            </template>
            <span>
              インターネット: {{ item.online ? 'オンライン' : 'オフライン' }}
            </span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                large
                v-on="on"
                :color="getCaptureStateField(item, 'color')"
                >mdi-camera</v-icon
              >
            </template>
            <span>
              {{ getCaptureStateField(item) }}
            </span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                large
                v-on="on"
                :color="getDisplayStateField(item, 'color')"
                >mdi-monitor</v-icon
              >
            </template>
            <span>
              {{ getDisplayStateField(item) }}
            </span>
          </v-tooltip>
        </template>

        <template v-slot:[`item.lastEventDate`]="{ item }">
          {{ showLastEventReceived(item.id) }}
        </template>

        <template v-slot:[`item.watchDevice`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-edit-dialog
                  large
                  :return-value.sync="item.interval"
                  @save="saveInterval(item.id, item.interval)"
                >
                  <v-btn small icon left>
                    <v-icon>mdi-playlist-edit</v-icon>
                  </v-btn>
                  <span>{{ showIntervalName(item.interval) }}</span>
                  <template v-slot:input>
                    <v-select
                      v-model="item.interval"
                      :items="intervals"
                      item-text="name"
                      item-value="milliSeconds"
                    />
                  </template>
                </v-edit-dialog>
              </div>
            </template>
            <span>指定された時間内にデータ送信がなかった場合に実行</span>
          </v-tooltip>
        </template>

        <template v-slot:[`item.notifSettings`]="{ item }">
          <div @click="onOpenNotifSettings(item)">
            <div v-if="isNotif(item.id)">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" x-large color="success">
                    mdi-cog
                  </v-icon>
                </template>
                <span>メール通知が設定されています</span>
              </v-tooltip>
            </div>
            <div v-else>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" x-large>
                    mdi-cog-off
                  </v-icon>
                </template>
                <span>メール通知は設定されていません</span>
              </v-tooltip>
            </div>
          </div>
        </template>

        <template v-slot:[`item.reboot`]="{ item }">
          <v-btn
            color="red"
            @click="onRebootClicked(item)"
            :disabled="!item.online"
          >
            再起動
          </v-btn>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-chip
            label
            @click="openSchedule(item)"
            dark
            color="device"
            class="mr-2"
          >
            <v-icon left>mdi-calendar</v-icon>スケジュール</v-chip
          >
          <v-chip
            label
            @click="openDetails(item)"
            dark
            color="device"
            class="mr-2"
          >
            <v-icon left>mdi-details</v-icon>
            詳細
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <notif-set-dialog
      :dialogOpen="notifSettingsOpen"
      :selectedDevice="selectedDevice"
      :deviceNotifSettings="deviceNotifSettings"
      @escPushed="onCloseNotifSettings"
      @outsideClicked="onCloseNotifSettings"
    >
    </notif-set-dialog>

    <confirm-modal
      :dialogOpen="rebootDialog"
      title="デバイス再起動確認"
      message="デバイスの電源を入れ直し、システムを再起動します。再起動には5分ほどかかります。"
      :dialogLoading="rebootDialogLoding"
      yesActionName="再起動を実行"
      noActionName="キャンセル"
      @escPushed="cancelReboot"
      @noClicked="cancelReboot"
      @yesClicked="excuteReboot"
      @outsideClicked="cancelReboot"
    >
    </confirm-modal>
    <info-modal
      :dialogOpen="infoDialog"
      title="再起動の命令を送りました"
      message="再起動には5分ほどかかります。再起動が完了したかは、5分後以降に確認してください。"
      @escPushed="openInfoDialog"
      @okClicked="closeInfoDialog"
      @outsideClicked="openInfoDialog"
    >
    </info-modal>
  </v-container>
</template>

<script>
import { Pick } from '@/protocol'
import { functions } from '@/config/firebase'
import firebase from 'firebase'
import { mapActions, mapGetters } from 'vuex'
import { deviceFunctionsMixin } from '@/plugins/mixins'
import DeviceUnitEditDialog from '@/components/device/DeviceUnitEditDialog'
import DeviceGroupEditDialog from '@/components/device/DeviceGroupEditDialog'
import DeviceUnitCreateDialog from '@/components/device/DeviceUnitCreateDialog'
import DeviceGroupCreateDialog from '@/components/device/DeviceGroupCreateDialog'
import DeviceScheduleDialog from '@/components/device/DeviceScheduleDialog'
import {
  updateDevice,
  upsertDeviceProps
} from '@/firebaseAccess/firestoreAccess'
import { rebootRemoteDevice } from '@/firebaseAccess/functionsAccess'
import ConfirmModal from '@/components/dialogs/ConfirmModal'
import InfoModal from '@/components/dialogs/InfoModal'
import NotifSetDialog from '@/components/devices/NotifSetDialog.vue'

const intervalList = [
  { name: 'なし', milliSeconds: 0 },
  { name: '30分間', milliSeconds: 30 * 60 * 1000 },
  { name: '1時間', milliSeconds: 60 * 60 * 1000 },
  { name: '3時間', milliSeconds: 3 * 60 * 60 * 1000 },
  { name: '6時間', milliSeconds: 6 * 60 * 60 * 1000 },
  { name: '12時間', milliSeconds: 12 * 60 * 60 * 1000 }
]

const appModeList = [
  { name: '未設定', mode: Pick.Device.Config.Mode.MODE_UNSPECIFIED },
  { name: 'デモモード', mode: Pick.Device.Config.Mode.MODE_DEMO },
  {
    name: 'ディスプレイモード',
    mode: Pick.Device.Config.Mode.MODE_PLAYER
  },
  { name: '分析モード', mode: Pick.Device.Config.Mode.MODE_ANALYSIS },
  { name: 'アイトラッキングモード', mode: Pick.Device.Config.Mode.MODE_GAZE }
]

const zeroPadding = num => {
  return ('0' + num).slice(-2)
}

const formatDate = eventDate => {
  const year = eventDate.getFullYear()
  const month = zeroPadding(eventDate.getMonth() + 1)
  const date = zeroPadding(eventDate.getDate())
  const hour = zeroPadding(eventDate.getHours())
  const minute = zeroPadding(eventDate.getMinutes())
  const seconds = zeroPadding(eventDate.getSeconds())

  return `${year}/${month}/${date} ${hour}:${minute}:${seconds}`
}

export default {
  name: 'DashboardDevices',
  components: {
    DeviceUnitEditDialog,
    DeviceGroupEditDialog,
    DeviceUnitCreateDialog,
    DeviceGroupCreateDialog,
    DeviceScheduleDialog,
    ConfirmModal,
    InfoModal,
    NotifSetDialog
  },
  mixins: [deviceFunctionsMixin],
  data: () => ({
    devicesLoading: true,

    scheduleDialog: false,
    editUnitDialog: false,
    editGroupDialog: false,
    createUnitDialog: false,
    createGroupDialog: false,

    selectedDevice: {},
    selectedUnit: {},
    selectedGroup: {},

    unitFilters: [],
    groupFilters: [],

    appModes: appModeList,
    modeMenuOpen: false,
    deviceComposition: {
      unknown: 0,
      single: 1,
      unit: 2,
      device: 3
    },

    rebootDialog: false,
    rebootDialogLoding: false,
    rebootTargetDevice: {},

    infoDialog: false,

    lastEventNotifies: {},
    intervals: intervalList,

    notifSettingsOpen: false,
    deviceNotifSettings: {}
  }),
  computed: {
    ...mapGetters({
      devices: 'devices/getDevices',
      units: 'devices/units/getUnits',
      groups: 'devices/groups/getGroups',
      schedules: 'schedules/getSchedules',
      events: 'schedules/events/getScheduleEvents',
      reports: 'reports/getReports',
      notifs: 'users/notifs/getNotifs'
    }),
    isAllUnitFilter() {
      return this.unitFilters.length === this.units.length
    },
    isAllGroupFilter() {
      return this.groupFilters.length === this.groups.length
    },
    headers() {
      return [
        { text: 'デバイス名', value: 'name' },
        { text: 'ID', value: 'id' },
        {
          text: 'ユニット',
          value: 'units',
          filter: value => {
            if (this.isAllUnitFilter) return true
            const ids = this.unitFilters.map(i => this.units[i]).map(u => u.id)
            return value.some(id => ids.includes(id))
          }
        },
        {
          text: 'グループ',
          value: 'groups',
          filter: value => {
            if (this.isAllGroupFilter) return true
            const ids = this.groupFilters
              .map(i => this.groups[i])
              .map(g => g.id)
            return value.some(id => ids.includes(id))
          }
        },
        { text: 'モード', value: 'mode' },
        { text: 'ステータス', value: 'online', filterable: false },
        { text: '最終イベント', value: 'lastEventDate' },
        { text: '自動再起動', value: 'watchDevice' },
        { text: '死活管理', value: 'notifSettings' },
        { text: '', value: 'reboot' },
        {
          text: '',
          value: 'actions',
          align: 'end',
          sortable: false,
          filterable: false
        }
      ]
    }
  },
  methods: {
    ...mapActions({
      showSnack: 'snackbar/showSnack',

      bindDevicesRef: 'devices/bindRef',

      bindDeviceUnitsRef: 'devices/units/bindRef',
      bindDeviceGroupsRef: 'devices/groups/bindRef',

      bindSchedulesRef: 'schedules/bindRef',
      bindScheduleEventsRef: 'schedules/events/bindRef',
      bindPlaylistsRef: 'playlists/bindRef',

      bindReportRef: 'reports/bindRef',
      bindNotifRef: 'users/notifs/bindRef'
    }),
    clearMenu(item) {
      this.$refs['unit-' + item.id].value = false
      this.$refs['group-' + item.id].value = false
    },
    closeAllDialogs() {
      this.editUnitDialog = false
      this.editGroupDialog = false
      this.createUnitDialog = false
      this.createGroupDialog = false
      this.scheduleDialog = false
    },
    getCaptureStateField(device, field = 'text') {
      let state = null
      // Get camera capture state according to its value.
      if (
        device.online &&
        device.state !== undefined &&
        device.state.capture !== undefined
      ) {
        state = this.getDeviceCaptureState().find(
          s => s.value === device.state.capture
        )
      }

      if (field === 'text') {
        const stateText = state ? state[field] : `---`
        return `カメラ:${stateText}`
      } else if (field === 'color') {
        return state ? state[field] : 'gray'
      }
    },
    getDisplayStateField(device, field = 'text') {
      let state = null
      // Get moniter display state according to its value.
      if (
        device.online &&
        device.state !== undefined &&
        device.state.display !== undefined
      ) {
        state = this.getDeviceDisplayState().find(
          s => s.value === device.state.display
        )
      }

      if (field === 'text') {
        const stateText = state ? state[field] : ' ---'
        return `モニター:${stateText}`
      } else if (field === 'color') {
        return state ? state[field] : 'gray'
      }
    },
    getDeviceUnitName(unitId) {
      const unit = this.units.find(u => u.id === unitId)
      return unit === undefined ? '--' : unit.name
    },
    getDeviceGroupName(groupId) {
      const group = this.groups.find(g => g.id === groupId)
      return group === undefined ? '--' : group.name
    },
    editUnit(device, unitId) {
      this.selectedDevice = device
      this.selectedUnit = { id: unitId, name: this.getDeviceUnitName(unitId) }
      this.editUnitDialog = true
    },
    editGroup(device, groupId) {
      this.selectedDevice = device
      this.selectedGroup = {
        id: groupId,
        name: this.getDeviceGroupName(groupId)
      }
      this.editGroupDialog = true
    },
    removeUnitFromDevice(device, unitId) {
      device.units = device.units.filter(i => i !== unitId)
      updateDevice(device.id, device)
    },
    removeGroupFromDevice(device, groupId) {
      device.groups = device.groups.filter(i => i !== groupId)
      updateDevice(device.id, device)
    },
    openSchedule(device) {
      this.scheduleDialog = true
      this.selectedDevice = device
    },
    openDetails(device) {
      this.$router.push({ path: `/client/device/${device.id}` })
    },
    saveDevice(device) {
      updateDevice(device.id, device)
        .then(() => {
          this.showSnack({ message: '保存されました' })
        })
        .catch(err => {
          this.showSnack({
            message: `保存に失敗しました: ${err}`,
            color: 'error'
          })
        })
        .finally(() => {
          this.editUnitDialog = false
          this.editGroupDialog = false
          this.createUnitDialog = false
          this.createGroupDialog = false
        })
    },
    appModeName(mode) {
      return appModeList.find(a => a.mode === mode).name
    },
    showSaveSnack() {
      this.showSnack({
        message: 'デバイス設定の更新を保存しました',
        color: 'success'
      })
    },
    showErrorSnack(message) {
      this.showSnack({
        message: `デバイス設定更新の保存に失敗しました: ${message}`,
        color: 'error'
      })
    },
    async updateMode(composition, deviceId, mode) {
      // Notify app mode.
      switch (composition) {
        case this.deviceComposition.single:
          await updateDevice(deviceId, {
            mode: mode,
            updated: firebase.firestore.Timestamp.now()
          })
          break
        case this.deviceComposition.unit:
          break
        case this.deviceComposition.group:
          break
        default:
          return
      }

      this.updateDeviceConfig(deviceId)
    },
    updateDeviceConfig(deviceId) {
      this.devicesLoading = true

      const task = functions.httpsCallable('device-UpdateDevicesConfig')
      task({ deviceIds: [deviceId] })
        .then(result => {
          if (result.data.success) {
            this.showSaveSnack()
          } else {
            this.showErrorSnack(result.data.message)
          }
        })
        .catch(error => {
          this.showErrorSnack(error)
        })
        .finally(() => {
          this.devicesLoading = false
        })
    },
    showIntervalName(intervalMilliSeconds) {
      let interval = this.intervals.find(
        i => i.milliSeconds === intervalMilliSeconds
      )
      if (!interval || !intervalMilliSeconds) {
        interval = this.intervals[0]
      }

      return interval.name
    },
    async saveInterval(deviceId, intervalMilliSeconds) {
      await upsertDeviceProps(deviceId, {
        interval: intervalMilliSeconds
      })
    },
    showLastEventReceived(deviceId) {
      let lastDate = null
      for (const report of this.reports) {
        if (report.id === deviceId) {
          lastDate = report.received.toDate()
          break
        }
      }

      let lastDateStr = '----/--/-- --:--:--'
      if (lastDate) {
        lastDateStr = formatDate(lastDate)
      }

      return lastDateStr
    },
    onRebootClicked(device) {
      this.rebootTargetDevice = device
      this.rebootDialog = true
    },
    closeRebootDialog() {
      this.rebootTargetDevice = {}
      this.rebootDialog = false
    },
    cancelReboot() {
      this.showSnack({
        message: '再起動をキャンセルしました',
        color: 'info'
      })

      this.closeRebootDialog()
    },
    async excuteReboot() {
      if (!this.rebootTargetDevice || this.rebootTargetDevice.id.length === 0) {
        this.showSnack({
          message: 'デバイスが見つかりませんでした',
          color: 'error'
        })

        this.closeRebootDialog()
      }

      this.rebootDialogLoding = true

      const response = await rebootRemoteDevice(this.rebootTargetDevice.id)

      if (response.data.success) {
        // Open Info dialog to explain that reboot action takes time.
        this.openInfoDialog()
      } else {
        this.showSnack({
          message: '再起動に失敗しました',
          color: 'error'
        })
      }

      this.rebootDialogLoding = false

      this.closeRebootDialog()
    },
    openInfoDialog() {
      this.infoDialog = true
    },
    closeInfoDialog() {
      this.infoDialog = false
    },
    isNotif(deviceId) {
      const notifSettings = this.notifs.find(n => n.id === deviceId)
      if (notifSettings) {
        return notifSettings.active
      }
      return false
    },
    onOpenNotifSettings(device) {
      this.selectedDevice = device
      let notifSettings = this.notifs.find(n => n.id === device.id)
      if (!notifSettings) {
        notifSettings = {}
      }

      this.deviceNotifSettings = notifSettings

      this.notifSettingsOpen = true
    },
    onCloseNotifSettings() {
      this.selectedDevice = {}

      this.notifSettingsOpen = false
    }
  },
  async created() {
    await this.bindDevicesRef()
      .then(() => {
        // Set interval prop if device data doesn't have.
        for (const device of this.devices) {
          if (!device.interval) {
            device.interval = 0
          }
        }

        return this.bindReportRef()
      })
      .then(() => {
        for (const report of this.reports) {
          let notify = false
          if ('notify' in report) {
            notify = report.notify
          }

          this.lastEventNotifies[report.id] = notify
        }

        return this.bindNotifRef()
      })
      .then(() => {
        return this.bindDeviceGroupsRef()
      })
      .then(() => {
        // set all group filters on by default
        this.groupFilters = this.groups.map(g => this.groups.indexOf(g))
        return this.bindDeviceUnitsRef()
      })
      .then(() => {
        // set all unit filters on by default
        this.unitFilters = this.units.map(u => this.units.indexOf(u))
        return this.bindSchedulesRef()
      })
      .then(() => {
        this.selectedSchedule = this.schedules[0]
        return this.bindScheduleEventsRef(this.selectedSchedule.id)
      })
      .finally(() => {
        this.devicesLoading = false
      })
  }
}
</script>

<style scoped></style>
